import { Box, CircularProgress, Stack, TextField } from "@mui/material";
import { Button, ButtonVariantOption } from "@/components/Button";
import React, { useEffect, useMemo, useState } from "react";
import { PageTitle } from "@/components/Title";
import { GetCommercialMonitoringAsFileAPI } from "../api";
import { useLegalAffairsBureauName } from "../hooks/useLegalAffairsBureauName";
import { CommercialMonitoringReceptionReasonSelect } from "../components/CommercialMonitoringReceptionReasonSelect";
import {
  type AxiosResponse,
  HttpStatusCode,
  isAxiosError,
  type AxiosInstance,
} from "axios";
import { CommercialMonitoringResultDataGrid } from "../components/CommercialMonitoringResultDataGrid";
import { useNavigate } from "react-router-dom";
import {
  type GetCommercialMonitoringDetailsAPIResponse,
  type CommercialMonitoringDetailEntity,
  type CommercialMonitoringResultRow,
} from "../types";
import { toast } from "react-toastify";
import { type DialogProps } from "@mui/material/Dialog/Dialog";
import { SendToTrackingService } from "@/api/handlers/errors";
import { Label } from "@/components/Label";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CustomModal } from "@/components/Modal";
import { getReceptionBookReasonNames } from "../utils/getReceptionBookReasonNames";

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  apiClient: AxiosInstance;
  monitoringId: number | null;
}

/**
 * 商業登記モニタリング結果モーダル
 * 不動産の結果モーダルと異なり、チェックボックスでの即時登記取得は持たず、
 * 「商業登記取得」ボタンから既存の商業登記取得依頼フロー（/commercialbook）へ遷移する。
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
export const CommercialMonitoringResultModal: React.FC<Props & DialogProps> = (
  props
) => {
  const { open, setOpen, onClose, apiClient, monitoringId } = props;
  const navigate = useNavigate();
  const resolveLegalAffairsBureauName = useLegalAffairsBureauName();

  // 検索項目
  const [searchCorporateName, setSearchCorporateName] = useState<string>(""); // 法人名
  const [
    searchCommercialRegistrationNumber,
    setSearchCommercialRegistrationNumber,
  ] = useState<string>(""); // 会社法人等番号
  const [searchReceptionReasons, setSearchReceptionReasons] = useState<
    string[]
  >([]); // 登記原因（複数選択可。いずれかの登記原因に一致すればヒットする）

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [receptionReasonOptions, setReceptionReasonOptions] = useState<
    Array<{ label: string; value: string }>
  >([]); // 登記原因の選択肢一覧
  const [rawDetails, setRawDetails] = useState<
    CommercialMonitoringDetailEntity[]
  >([]); // モニタリング結果APIレスポンス（法務局名解決前）

  /**
   * resultsの初期値を設定する。
   * @param {CommercialMonitoringDetailEntity[]} details
   * @returns {CommercialMonitoringResultRow[]}
   */
  const initialize = (
    details: CommercialMonitoringDetailEntity[]
  ): CommercialMonitoringResultRow[] => {
    return details.map((detail) => {
      const gridRow: CommercialMonitoringResultRow = {
        id: detail.id,
        commercialRegistrationNumber:
          detail.requesterCommercialRegistrationNumber,
        corporateName: detail.corporateName,
        legalAffairsBureauName: resolveLegalAffairsBureauName(
          detail.legalAffairsBureauId
        ),
        registrationDate: detail.registrationDate,
        remarks: detail.remarks,
        receptionReasonIds: [],
      };
      if (detail.monitoringResult) {
        gridRow.requestDate =
          detail.monitoringResult.legalAffairsBureauRequestDate;
        gridRow.receptionReasonIds =
          detail.monitoringResult.commercialReceptionReasons.map(
            (receptionReason) => receptionReason.id
          );
        gridRow.receptionReason = getReceptionBookReasonNames(
          detail.monitoringResult.commercialReceptionReasons
        );
      }
      return gridRow;
    });
  };

  // NOTE: 法務局名解決(resolveLegalAffairsBureauName)の元データがモーダル表示より
  // 後にロードされても表示に反映されるよう、useMemoの依存に含めて表示時に解決する
  const defaultRows = useMemo<CommercialMonitoringResultRow[]>(
    () => initialize(rawDetails),
    [rawDetails, resolveLegalAffairsBureauName]
  );

  // CSVダウンロード処理
  const handleCsvDownloadButtonClick = (): void => {
    GetCommercialMonitoringAsFileAPI(apiClient, {
      ids: [Number(monitoringId)],
    }).catch((error) => {
      if (
        isAxiosError<AxiosResponse>(error) &&
        error.response?.status === HttpStatusCode.BadRequest
      ) {
        toast.error("CSVファイルのダウンロードに失敗しました。");
        return;
      }
      if (
        isAxiosError<AxiosResponse>(error) &&
        (error.response?.status ?? HttpStatusCode.InternalServerError) >=
          HttpStatusCode.InternalServerError
      ) {
        SendToTrackingService(error);
        toast.error("CSVファイルのダウンロードに失敗しました");
      }
    });
  };

  // 「商業登記取得」ボタン押下時の処理
  // 既存の商業登記取得依頼フロー（検索 → 法人一覧 → 取得依頼）へ、会社法人等番号・法人名を引き継いで遷移する
  const handleClickAcquireCommercialBook = (
    row: CommercialMonitoringResultRow
  ): void => {
    const params = new URLSearchParams({
      companyName: row.corporateName,
      corporateNumber: row.commercialRegistrationNumber,
    });
    navigate(`/commercialbook?${params.toString()}`);
  };

  // データ取得処理
  useEffect(() => {
    const fetchMonitoringDetails = async (): Promise<
      GetCommercialMonitoringDetailsAPIResponse | undefined
    > => {
      try {
        const monitoringDetails =
          await apiClient.get<GetCommercialMonitoringDetailsAPIResponse>(
            `/commercial-monitoring/${String(monitoringId)}`,
            {
              params: {
                getMonitoringResult: true,
              },
            }
          );
        return monitoringDetails.data;
      } catch (error) {
        setOpen(false);
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Unauthorized
        ) {
          toast.info("再度該当のリンクを押下してください");
          return;
        }
        if (
          isAxiosError(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          toast.error("モニタリング結果の取得に失敗しました");
          SendToTrackingService(error);
        }
      }
    };

    (async () => {
      if (open && monitoringId) {
        // 初期化処理
        setSearchCorporateName("");
        setSearchCommercialRegistrationNumber("");
        setSearchReceptionReasons([]);

        // 検索処理
        setIsLoading(true);
        const details = await fetchMonitoringDetails();
        const receptionReasonMap = new Map<
          string,
          { label: string; value: string }
        >();
        const allReceptionReasons = (details?.details ?? []).flatMap(
          (detail) => detail.monitoringResult?.commercialReceptionReasons ?? []
        );
        for (const receptionReason of allReceptionReasons) {
          receptionReasonMap.set(String(receptionReason.id), {
            label: receptionReason.name,
            value: String(receptionReason.id),
          });
        }
        setReceptionReasonOptions(
          Array.from(receptionReasonMap.values()).sort((a, b) => {
            return Number(a.value) - Number(b.value);
          })
        );
        setRawDetails(details?.details ?? []);
        setIsLoading(false);
      }
    })();
  }, [open, monitoringId]);

  // 検索でのフィルタリング（defaultRows・検索条件から導出される表示用データ）
  const filteredRows = useMemo(() => {
    let rows = defaultRows;
    if (searchCorporateName) {
      rows = rows.filter((row) => {
        return row.corporateName.includes(searchCorporateName);
      });
    }
    if (searchCommercialRegistrationNumber) {
      rows = rows.filter((row) => {
        return row.commercialRegistrationNumber.includes(
          searchCommercialRegistrationNumber
        );
      });
    }
    if (searchReceptionReasons.length > 0) {
      rows = rows.filter((row) => {
        return row.receptionReasonIds.some((id) =>
          searchReceptionReasons.includes(String(id))
        );
      });
    }
    return rows;
  }, [
    defaultRows,
    searchCorporateName,
    searchCommercialRegistrationNumber,
    searchReceptionReasons,
  ]);

  return (
    <CustomModal
      isOpen={open}
      handleClose={(event, reason) => {
        setOpen(false);
        if (onClose) {
          onClose(event, reason);
        }
      }}
      ariaLabelledby={"commercial-monitoring-result-modal"}
      ariaDescribedby={"commercial-monitoring-result-modal"}
      height={"90%"}
      width={"90%"}
      overflow={"scroll"}
    >
      <PageTitle>モニタリング結果</PageTitle>

      {/* 検索項目 */}
      <Box sx={{ mx: 2, mb: 2, display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Label>法人名</Label>
          <TextField
            id="searchCorporateName"
            label="法人名"
            value={searchCorporateName}
            fullWidth
            onChange={(event) => {
              setSearchCorporateName(event.target.value);
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Label>会社法人等番号</Label>
          <TextField
            id="searchCommercialRegistrationNumber"
            label="会社法人等番号"
            value={searchCommercialRegistrationNumber}
            fullWidth
            onChange={(event) => {
              setSearchCommercialRegistrationNumber(event.target.value);
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Label>登記原因</Label>
          <CommercialMonitoringReceptionReasonSelect
            value={searchReceptionReasons}
            onChange={setSearchReceptionReasons}
            options={receptionReasonOptions}
          />
        </Box>
      </Box>

      {/* 結果 */}
      <Box sx={{ mt: 3 }}>
        {isLoading ? (
          <Stack alignItems="center">
            <CircularProgress sx={{ my: "20px" }} />
          </Stack>
        ) : (
          <>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button
                label="CSVダウンロード"
                onClick={handleCsvDownloadButtonClick}
                variant={ButtonVariantOption.Contained}
                size={SizingWrapperStyle.MEDIUM}
              />
            </Box>
            <CommercialMonitoringResultDataGrid
              navigate={navigate}
              rows={filteredRows}
              handleClickAcquireCommercialBook={
                handleClickAcquireCommercialBook
              }
            />
          </>
        )}
      </Box>
    </CustomModal>
  );
};
